// React hook for the Intel SSE feed.
//
// Lifecycle:
//   1. Mount → open IDB cache, hydrate state from the most recent N rows so
//      the page renders instantly (no network blocking).
//   2. Initial pull via TanStack Query against /v1/intel/signals?<filter>.
//      Merge into state (cache + state) keyed by signal id.
//   3. Open SSE. Live frames merge into state and persist to IDB. Server
//      replays from `Last-Event-ID` so a reconnect never drops a frame.
//   4. Unmount → stop SSE; cache stays open for the next mount.
//
// Filtering:
//   The hook applies `state` client-side post-merge so a filter change does
//   not refetch (one filter state, three views, zero re-fetch). The initial
//   pull uses the filter state to bound payload size; any signal that
//   arrives via SSE outside the active filter is still cached for instant
//   tab-switch but excluded from the returned `rows`.

import type { filters as f, intel as i } from '@eidollon/contracts';
import { intel as ic } from '@eidollon/contracts';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { type IntelCache, openIntelCache } from './idb/cache.js';
import { type ConnectionStatus, type IntelSseClient, createIntelSseClient } from './sse/client.js';

export interface UseIntelFeedOptions {
  /** Full SSE URL — e.g. `https://api.eidollon.app/v1/realtime/intel/sse`. */
  sseUrl: string;
  /** Initial-pull URL — e.g. `https://api.eidollon.app/v1/intel/signals`. */
  pullUrl: string;
  /** Access JWT (15-min). Renew + re-call on rotation. */
  token: string;
  /** Seed cap on the initial IDB hydration. Defaults to the cache CAP. */
  hydrateLimit?: number;
  /** Optional: fires once per live frame after merge, with the new signals
   *  in arrival order. Used by AlertTape to render slide-in chips. The
   *  callback runs *after* the SDK has merged + persisted the rows. */
  onLiveEvent?: (signals: i.IntelSignal[]) => void;
  /** When true, live SSE frames are NOT merged into the visible state — the
   *  feed freezes at its current rows until unpaused. The SSE connection stays
   *  open (and frames are still persisted to IDB + tracked via Last-Event-ID
   *  so no replay gap on resume), but the in-memory `byId` map the UI renders
   *  from is left untouched. Flipping this off resumes merging on the next
   *  frame. Toggling it does NOT reopen the connection. */
  paused?: boolean;
}

export interface UseIntelFeedResult {
  rows: i.IntelSignal[];
  total: number;
  status: ConnectionStatus;
  error: string | null;
}

// Permissive on extra top-level keys so the SDK is forward-compatible with
// new API metadata (cursor, count, meta) without coordinated frontend ships.
// Each item is still strictly validated via ic.IntelSignal.
const PullResponse = z.object({
  items: z.array(ic.IntelSignal),
});

export function useIntelFeed(state: f.FilterState, opts: UseIntelFeedOptions): UseIntelFeedResult {
  const cacheRef = useRef<IntelCache | null>(null);
  const sseRef = useRef<IntelSseClient | null>(null);
  const liveCbRef = useRef<UseIntelFeedOptions['onLiveEvent']>(opts.onLiveEvent);
  liveCbRef.current = opts.onLiveEvent;
  // Keep `paused` on a ref so the SSE handler reads the live value without the
  // SSE useEffect (keyed on url/token/cacheReady) tearing down + reconnecting
  // every time the pause button flips. The connection stays hot while paused;
  // we just skip the visible merge.
  const pausedRef = useRef<boolean>(opts.paused ?? false);
  pausedRef.current = opts.paused ?? false;
  const [byId, setById] = useState<Map<string, i.IntelSignal>>(new Map());
  const [status, setStatus] = useState<ConnectionStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  // HIGH-8 (correctness): the SSE useEffect used to fire before the IDB
  // cache resolved, so the resume cursor was always null on cold start →
  // missed backfill. Gate SSE on `cacheReady` and flip the flag once the
  // cache.open promise settles.
  const [cacheReady, setCacheReady] = useState(false);

  // ── 1. Open cache + hydrate from IDB on mount ────────────────────────────
  // biome-ignore lint/correctness/useExhaustiveDependencies: hydrateLimit is intentionally read once at mount; an opts identity churn must not reopen the cache.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const cache = await openIntelCache();
        if (!alive) {
          cache.close();
          return;
        }
        cacheRef.current = cache;
        const seed = await cache.recent(opts.hydrateLimit);
        if (!alive) return;
        setById((prev) => mergeMany(prev, seed));
        setCacheReady(true);
      } catch (e) {
        setError(toMessage(e));
        // Flip ready anyway so the page falls back to live-only (no cache).
        // Better degraded-mode than a blocked SSE forever.
        if (alive) setCacheReady(true);
      }
    })();
    return () => {
      alive = false;
      cacheRef.current?.close();
      cacheRef.current = null;
      setCacheReady(false);
    };
  }, []);

  // ── 2. Initial pull (TanStack Query) ─────────────────────────────────────
  const pull = useQuery({
    queryKey: ['intel-feed', 'initial', opts.pullUrl, opts.token, JSON.stringify(state)],
    queryFn: async (): Promise<i.IntelSignal[]> => {
      // Backend accepts POST with { state, sort, cursor, limit } per
      // services/api/src/routes/intel.ts SignalsRequest. EventSource is
      // header-blind (Agent S patched the SSE side); the JSON pull uses
      // both Authorization header AND credentials so the access cookie
      // works whether or not the bearer is present.
      // has_geom=true — the Intel page is map-first; OFAC sanctions (entity-
      // keyed, no lat/lng) would otherwise flood the most-recent page and
      // leave the map empty. Critically, this is ALSO what drives the right-
      // side "non-geo results" card on the map view: that panel appears
      // precisely when the geom-only feed returns few plottable rows yet the
      // filter matches many (geomless) signals — see `matchesButNonePlottable`
      // in routes/_authed.intel.tsx. Sending has_geom=false here fills the feed
      // with 200 sanctions, pushing signals.length to the cap, which SUPPRESSES
      // that card. So the map feed stays geom-only; geomless types surface in
      // the non-geo card (map) and natively in Timeline/Analytics.
      const res = await fetch(opts.pullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(opts.token ? { Authorization: `Bearer ${opts.token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({ state, limit: 200, has_geom: true }),
      });
      if (!res.ok) throw new Error(`pull ${res.status}`);
      const body = PullResponse.parse(await res.json());
      return body.items;
    },
    // 60s so toggling a facet on/off/on within the window is a cache hit on
    // the initial pull (the client predicate re-narrows the cached rows for
    // free; only a server-authoritative field change needs a fresh pull).
    staleTime: 60_000,
  });

  useEffect(() => {
    const data = pull.data;
    if (!data) return;
    setById((prev) => mergeMany(prev, data));
    cacheRef.current?.putMany(data).catch(() => {
      /* cache best-effort */
    });
  }, [pull.data]);

  // ── 3. Open SSE ──────────────────────────────────────────────────────────
  // HIGH-8: gate on cacheReady so the cursor read sees the actual hydrated
  // store, not the racing ref. The cacheReady flag flips after the cache
  // either resolved or errored.
  useEffect(() => {
    if (!cacheReady) return;
    let alive = true;
    let unsub: (() => void) | null = null;
    // Hoisted so the outer useEffect cleanup can actually clear it. The
    // previous shape returned a clearInterval cleanup from the inner async
    // IIFE — React never sees that return value, so the 1-Hz status poll
    // leaked one timer per mount/unmount cycle.
    let statusTimer: ReturnType<typeof setInterval> | null = null;
    (async () => {
      const lastId = (await cacheRef.current?.getLastEventId().catch(() => null)) ?? null;
      if (!alive) return;
      const client = createIntelSseClient({
        url: opts.sseUrl,
        token: opts.token,
        ...(lastId ? { lastEventId: lastId } : {}),
      });
      sseRef.current = client;
      unsub = client.on(async (frame) => {
        const incoming: i.IntelSignal[] =
          frame.kind === 'signal' ? [frame.signal] : frame.batch.items;
        // Always persist to IDB + advance the resume cursor, even while paused
        // — that keeps the cache warm and guarantees no Last-Event-ID replay
        // gap when the user resumes. Only the *visible* merge and the live
        // callback are gated on pause.
        cacheRef.current?.putMany(incoming).catch(() => {
          /* best effort */
        });
        if (frame.eventId)
          cacheRef.current?.setLastEventId(frame.eventId).catch(() => {
            /* best effort */
          });
        // Paused → drop the frame from the visible state. The rows the UI
        // renders freeze; resuming merges the next (post-resume) frame. Rows
        // arriving while paused live only in IDB and surface on the next
        // initial pull / tab remount, so nothing is permanently lost.
        if (pausedRef.current) return;
        setById((prev) => mergeMany(prev, incoming));
        // Hand the live frame to consumers (AlertTape, etc.) — kept on the
        // ref so identity churn on the callback doesn't reopen the SSE.
        liveCbRef.current?.(incoming);
      });
      // poll status into React (cheap; the client mutates internally)
      statusTimer = setInterval(() => alive && setStatus(client.status()), 1000);
      client.start();
    })().catch((e) => setError(toMessage(e)));
    return () => {
      alive = false;
      if (statusTimer != null) clearInterval(statusTimer);
      statusTimer = null;
      unsub?.();
      sseRef.current?.stop();
      sseRef.current = null;
    };
  }, [opts.sseUrl, opts.token, cacheReady]);

  // ── 4. Apply filter (client-side, post-merge) ────────────────────────────
  const all = useMemo(() => Array.from(byId.values()), [byId]);
  const filtered = useMemo(() => applyFilter(all, state), [all, state]);

  return { rows: filtered, total: all.length, status, error };
}

/* ── helpers ──────────────────────────────────────────────────────────── */

function mergeMany(
  prev: Map<string, i.IntelSignal>,
  incoming: readonly i.IntelSignal[],
): Map<string, i.IntelSignal> {
  const next = new Map(prev);
  for (const s of incoming) next.set(s.id, s);
  return next;
}

function toMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** Same conjunctive semantics as `FilterState` everywhere else: AND across
 *  fields, OR within multi-enum. Mirrors the server's `filter-compiler.ts`
 *  whitelist for the *client-determinable* Intel signal fields only.
 *
 *  Fields filtered here (post-SSE merge, on the client) — every one is
 *  computable from the typed `IntelSignal` columns, so the predicate agrees
 *  with the server's SQL exactly:
 *    signal_type, source, severity (min/max range), severity_bucket,
 *    time_window.
 *
 *  Fields filtered server-side ONLY (initial pull): region/action_country,
 *  agency, naics, event_type, actor, fatalities, sanction_list,
 *  sanction_program, aoi, linked_to_opportunity. These are payload- or
 *  geometry-derived (or, like linked_to_opportunity, currently unpopulated in
 *  live data — every row has an empty linked_opportunity_ids array). The
 *  client must NOT re-apply them: doing so would disagree with server
 *  semantics and wrongly drop valid live SSE rows. Example of the bug this
 *  avoids: a client `linked_opportunity_ids.length > 0 === true` test drops
 *  100% of live frames because no live row carries a non-empty array, even
 *  though the server already narrowed the initial pull correctly. So for
 *  server-authoritative fields we keep the row and let the next initial pull
 *  (which re-queries the server) own the narrowing. */
export function applyFilter(rows: readonly i.IntelSignal[], state: f.FilterState): i.IntelSignal[] {
  // biome-ignore lint/complexity/useLiteralKeys: FilterState is a Zod record (index signature) and tsconfig's noPropertyAccessFromIndexSignature requires bracket access.
  const types = state['signal_type'];
  // biome-ignore lint/complexity/useLiteralKeys: FilterState is an index signature.
  const sources = state['source'];
  // biome-ignore lint/complexity/useLiteralKeys: FilterState is an index signature.
  const sev = state['severity'] as { min?: number | null; max?: number | null } | undefined;
  // biome-ignore lint/complexity/useLiteralKeys: FilterState is an index signature.
  const time = state['time_window'] as { from?: string | null; to?: string | null } | undefined;
  // biome-ignore lint/complexity/useLiteralKeys: FilterState is an index signature.
  const sevBucket = state['severity_bucket'];

  const typeSet = Array.isArray(types) ? new Set(types as string[]) : null;
  const sourceSet = Array.isArray(sources) ? new Set(sources as string[]) : null;
  // Bucket chips: build a numeric predicate from the contract boundaries so
  // the same severity → bucket mapping fires here, on the server, and in
  // the rendered chip label. Filters out any value that isn't a known
  // bucket label (defense in depth — the URL may have been hand-typed).
  const bucketSet = Array.isArray(sevBucket)
    ? new Set(
        (sevBucket as unknown[])
          .map((v) => (typeof v === 'string' ? v.toUpperCase() : ''))
          .filter(
            (v): v is ic.SeverityBucket =>
              v === 'HOT' || v === 'HIGH' || v === 'MED' || v === 'LOW',
          ),
      )
    : null;

  return rows.filter((r) => {
    if (typeSet && typeSet.size > 0 && !typeSet.has(r.signal_type)) return false;
    if (sourceSet && sourceSet.size > 0 && !sourceSet.has(r.source)) return false;
    if (sev) {
      if (sev.min != null && r.severity < sev.min) return false;
      if (sev.max != null && r.severity > sev.max) return false;
    }
    if (time) {
      if (time.from && r.occurred_at < time.from) return false;
      if (time.to && r.occurred_at > time.to) return false;
    }
    if (bucketSet && bucketSet.size > 0 && !bucketSet.has(ic.severityToBucket(r.severity)))
      return false;
    // linked_to_opportunity: guard with Array.isArray so SSE live rows that
    // omit linked_opportunity_ids entirely are not dropped (server already
    // narrowed the initial pull; live frames carry whatever the server emits).
    // biome-ignore lint/complexity/useLiteralKeys: FilterState index signature.
    const linkedFilter = state['linked_to_opportunity'];
    if (linkedFilter === true) {
      if (Array.isArray(r.linked_opportunity_ids) && r.linked_opportunity_ids.length === 0)
        return false;
    } else if (linkedFilter === false) {
      if (Array.isArray(r.linked_opportunity_ids) && r.linked_opportunity_ids.length > 0)
        return false;
    }
    // Other server-authoritative fields (region/action_country, agency, naics,
    // event_type, actor, fatalities, sanction_list, sanction_program, aoi) are
    // not re-applied here — keep the row and let the next server pull narrow.
    return true;
  });
}
